import React, { useState, useEffect } from 'react';
import { FiFilter, FiRotateCcw, FiEye, FiAlertCircle } from 'react-icons/fi';
import { fetchAuditLogsAPI, rollbackAuditLogAPI } from '../services/serverService';
import DiffViewerModal from '../components/DiffViewerModal';
import { useAuth } from '../context/AuthContext';

const AuditLogsPage = () => {
    // eslint-disable-next-line no-unused-vars
    const { currentUser } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Filters
    const [filters, setFilters] = useState({
        serverId: '',
        action: '',
        entityType: '',
        startDate: '',
        endDate: '',
        limit: 50
    });
    
    // Modal
    const [selectedLog, setSelectedLog] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = { ...filters };
            if (data.startDate) {
                const start = new Date(data.startDate);
                data.startDate = start.toISOString();
            }
            if (data.endDate) {
                const end = new Date(data.endDate);
                end.setUTCHours(23, 59, 59, 999);
                data.endDate = end.toISOString();
            }
            
            const response = await fetchAuditLogsAPI(data);
            if (response.data && response.data.status === 'success') {
                setLogs(response.data.data);
            } else {
                setError('Failed to fetch audit logs.');
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'Error fetching audit logs.');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = (e) => {
        e.preventDefault();
        fetchLogs();
    };

    const handleRollback = async (logId) => {
        if (!window.confirm("Are you sure you want to rollback this action? This will restore the previous data state and create a new audit log.")) {
            return;
        }
        
        try {
            const response = await rollbackAuditLogAPI(logId);
            if (response.data && response.data.status === 'success') {
                alert('Rollback successful.');
                fetchLogs();
            } else {
                alert('Rollback failed: ' + (response.data?.message || 'Unknown error'));
            }
        } catch (err) {
            console.error(err);
            alert('Error during rollback: ' + err.message);
        }
    };

    const openDiffModal = (log) => {
        setSelectedLog(log);
        setIsModalOpen(true);
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h1 style={{ margin: 0 }}>Global Audit Logs</h1>
                    <p style={{ margin: '5px 0 0', color: '#718096', fontWeight: 'bold' }}>Centralized system logging across all servers</p>
                </div>
            </div>

            {/* Filters Section */}
            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'flex-end' }}>
                <form onSubmit={applyFilters} style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', width: '100%', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>Server ID</label>
                        <input type="text" name="serverId" value={filters.serverId} onChange={handleFilterChange} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px' }} placeholder="All Servers" />
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>Action</label>
                        <select name="action" value={filters.action} onChange={handleFilterChange} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', backgroundColor: 'white' }}>
                            <option value="">All Actions</option>
                            <option value="CREATE">CREATE</option>
                            <option value="UPDATE">UPDATE</option>
                            <option value="DELETE">DELETE</option>
                            <option value="SUSPEND">SUSPEND</option>
                            <option value="ENABLE">ENABLE</option>
                            <option value="TOGGLE_STATUS">TOGGLE_STATUS</option>
                            <option value="ROLLBACK">ROLLBACK</option>
                        </select>
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>Entity Type</label>
                        <select name="entityType" value={filters.entityType} onChange={handleFilterChange} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', backgroundColor: 'white' }}>
                            <option value="">All Entities</option>
                            <option value="admin">admin</option>
                            <option value="employee">employee</option>
                            <option value="serviceUser">serviceUser</option>
                            <option value="server">server</option>
                        </select>
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>Start Date</label>
                        <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px' }} />
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>End Date</label>
                        <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: '4px' }} />
                    </div>
                    <div>
                        <button type="submit" className="add-button" style={{ backgroundColor: '#2b6cb0', display: 'flex', alignItems: 'center', height: '37px', padding: '0 20px' }}>
                            <FiFilter style={{ marginRight: '8px' }} /> Filter
                        </button>
                    </div>
                </form>
            </div>

            {/* Error State */}
            {error && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff5f5', border: '1px solid #fc8181', color: '#c53030', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                    <FiAlertCircle style={{ marginRight: '10px' }} /> {error}
                </div>
            )}

            {/* Logs Table */}
            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Timestamp</th>
                            <th>Server ID</th>
                            <th>Action / Entity</th>
                            <th>Performed By</th>
                            <th>Reason</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: '#a0aec0' }}>
                                    Loading logs...
                                </td>
                            </tr>
                        ) : logs.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: '#a0aec0' }}>
                                    No audit logs found for the given criteria.
                                </td>
                            </tr>
                        ) : (
                            logs.map(log => (
                                <tr key={log.id}>
                                    <td style={{ color: '#4a5568', fontSize: '14px' }}>
                                        {new Date(log.timestamp).toLocaleString()}
                                    </td>
                                    <td style={{ fontFamily: 'monospace', color: '#4a5568', fontSize: '14px' }}>
                                        {log.serverId || 'Global'}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold',
                                                backgroundColor: log.action === 'DELETE' ? '#fed7d7' : 
                                                               log.action === 'CREATE' ? '#c6f6d5' : 
                                                               log.action === 'SUSPEND' ? '#feebc8' : '#ebf8ff',
                                                color: log.action === 'DELETE' ? '#9b2c2c' : 
                                                       log.action === 'CREATE' ? '#22543d' : 
                                                       log.action === 'SUSPEND' ? '#c05621' : '#2b6cb0',
                                            }}>
                                                {log.action}
                                            </span>
                                            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#2d3748' }}>{log.entityType}</span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px', maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {log.entityId}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '14px', color: '#4a5568' }}>{typeof log.performedBy === 'object' ? log.performedBy?.email || 'System' : log.performedBy || 'System'}</div>
                                        <div style={{ fontSize: '12px', color: '#a0aec0', fontFamily: 'monospace', marginTop: '2px' }}>
                                            {typeof log.performedBy === 'object' ? log.performedBy?.uid : log.performedByUid}
                                        </div>
                                    </td>
                                    <td style={{ fontSize: '14px', color: '#4a5568', maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={log.reason}>
                                        {log.reason || '-'}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div className="actions-wrapper" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                            <button 
                                                className="action-button" 
                                                style={{ padding: '8px', fontSize: '1rem', background: '#ebf8ff', color: '#3182ce', border: '1px solid #90cdf4', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                onClick={() => openDiffModal(log)}
                                                title="View Changes"
                                            >
                                                <FiEye />
                                            </button>
                                            
                                            {/* Cannot rollback DELETE or CREATE easily and it's prohibited in backend */}
                                            {!['DELETE', 'CREATE'].includes(log.action) && (
                                                <button 
                                                    className="action-button" 
                                                    style={{ padding: '8px', fontSize: '1rem', background: '#fffaf0', color: '#dd6b20', border: '1px solid #fbd38d', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={() => handleRollback(log.id)}
                                                    title="Rollback this change"
                                                >
                                                    <FiRotateCcw />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Diff Modal */}
            <DiffViewerModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                log={selectedLog} 
            />
        </div>
    );
};

export default AuditLogsPage;
